/**
 * Error Export Utilities
 * 
 * Utility functions for exporting error data in different formats
 */

import { getTranslatedErrorMessage } from './BackendErrorTranslations';

/**
 * Export errors to text file
 * @param {Array} errors - Array of error objects
 * @param {Object} contextData - Context data for the errors
 * @param {string} mode - Mode of operation (create, edit, generic)
 * @param {Object} intl - React Intl object for translations
 */
export const exportErrorsToText = (errors, contextData, mode, intl) => {
  if (errors.length === 0) {
    console.warn('No errors to export');
    return;
  }

  try {
    const date = new Date();
    const dateStr = date.toISOString().split('T')[0];
    const timeStr = date.toTimeString().split(' ')[0].replace(/:/g, '-');
    const filename = `inventory_${mode}_errors_${dateStr}_${timeStr}.txt`;

    // Create text content
    const header = [
      intl.formatMessage({
        id: 'ui-plugin-inventory-templates.errorReport.title',
      }),
      '='.repeat(50),
      intl.formatMessage(
        { id: 'ui-plugin-inventory-templates.errorReport.generated' },
        { date: date.toLocaleString() },
      ),
      intl.formatMessage(
        { id: 'ui-plugin-inventory-templates.errorReport.instance' },
        {
          title:
            contextData?.instanceData?.title ||
            contextData?.title ||
            intl.formatMessage({
              id: 'ui-plugin-inventory-templates.instanceTitle.untitled',
            }),
        },
      ),
      intl.formatMessage(
        { id: 'ui-plugin-inventory-templates.errorReport.totalErrors' },
        { count: errors.length },
      ),
      '',
      intl.formatMessage({
        id: 'ui-plugin-inventory-templates.errorReport.summaryByCategory',
      }),
      '-'.repeat(30),
    ];

    // Count errors by category
    const errorCounts = errors.reduce((acc, error) => {
      acc[error.category] = (acc[error.category] || 0) + 1;
      return acc;
    }, {});

    Object.entries(errorCounts).forEach(([category, count]) => {
      header.push(
        `${category.charAt(0).toUpperCase() + category.slice(1)}: ${count} error(s)`,
      );
    });

    header.push('', '='.repeat(50), '');

    // Error details
    const errorDetails = errors.map((error, index) => {
      return [
        intl.formatMessage(
          { id: 'ui-plugin-inventory-templates.errorReport.errorNumber' },
          { number: index + 1 },
        ),
        '-'.repeat(30),
        intl.formatMessage(
          { id: 'ui-plugin-inventory-templates.errorReport.category' },
          { category: error.category },
        ),
        intl.formatMessage(
          { id: 'ui-plugin-inventory-templates.errorReport.operation' },
          {
            operation:
              error.operation ||
              intl.formatMessage({
                id: 'ui-plugin-inventory-templates.errorReport.unknownOperation',
              }),
          },
        ),
        intl.formatMessage(
          { id: 'ui-plugin-inventory-templates.errorReport.time' },
          { time: new Date(error.timestamp).toLocaleTimeString() },
        ),
        intl.formatMessage(
          { id: 'ui-plugin-inventory-templates.errorReport.message' },
          {
            message: getTranslatedErrorMessage(error, intl) ||
              intl.formatMessage({
                id: 'ui-plugin-inventory-templates.errorReport.noMessage',
              }),
          },
        ),
        error.code !== 'UNKNOWN'
          ? intl.formatMessage(
              { id: 'ui-plugin-inventory-templates.errorReport.errorCode' },
              { code: error.code },
            )
          : '',
        '',
      ]
        .filter((line) => line)
        .join('\n');
    });

    const content = [...header, ...errorDetails].join('\n');

    // Create and download file with UTF-8 BOM for proper encoding
    const BOM = '\uFEFF';
    const blob = new Blob([BOM + content], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.style.display = 'none';

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    // Clean up
    setTimeout(() => URL.revokeObjectURL(url), 100);

    console.log('Text file exported successfully:', filename);
  } catch (error) {
    console.error('Failed to export text file:', error);
  }
};

/**
 * Export errors to PDF file
 * @param {Array} errors - Array of error objects
 * @param {Object} contextData - Context data for the errors
 * @param {string} mode - Mode of operation (create, edit, generic)
 * @param {Object} intl - React Intl object for translations
 */
export const exportErrorsToPDF = async (errors, contextData, mode, intl) => {
  if (errors.length === 0) {
    console.warn('No errors to export');
    return;
  }

  try {
    // Dynamically import jsPDF to avoid build issues
    const { default: jsPDF } = await import('jspdf');

    const doc = new jsPDF();
    const date = new Date();
    const dateStr = date.toISOString().split('T')[0];
    const timeStr = date.toTimeString().split(' ')[0].replace(/:/g, '-');
    const filename = `inventory_${mode}_errors_${dateStr}_${timeStr}.pdf`;

    // Document setup
    const pageWidth = doc.internal.pageSize.width;
    const pageHeight = doc.internal.pageSize.height;
    const margin = 20;
    let yPosition = margin;

    // Title
    doc.setFontSize(18);
    doc.setFont(undefined, 'bold');
    doc.text(
      intl.formatMessage({
        id: 'ui-plugin-inventory-templates.errorReport.title',
      }),
      pageWidth / 2,
      yPosition,
      { align: 'center' },
    );
    yPosition += 15;

    // Header info
    doc.setFontSize(11);
    doc.setFont(undefined, 'normal');
    doc.text(
      intl.formatMessage(
        { id: 'ui-plugin-inventory-templates.errorReport.generated' },
        { date: date.toLocaleString() },
      ),
      margin,
      yPosition,
    );
    yPosition += 8;
    doc.text(
      intl.formatMessage(
        { id: 'ui-plugin-inventory-templates.errorReport.instance' },
        {
          title:
            contextData?.instanceData?.title ||
            contextData?.title ||
            intl.formatMessage({
              id: 'ui-plugin-inventory-templates.instanceTitle.untitled',
            }),
        },
      ),
      margin,
      yPosition,
    );
    yPosition += 8;
    doc.text(
      intl.formatMessage(
        { id: 'ui-plugin-inventory-templates.errorReport.totalErrors' },
        { count: errors.length },
      ),
      margin,
      yPosition,
    );
    yPosition += 12;

    // Summary by category
    doc.setFont(undefined, 'bold');
    doc.text(
      intl.formatMessage({
        id: 'ui-plugin-inventory-templates.errorReport.summaryByCategory',
      }),
      margin,
      yPosition,
    );
    yPosition += 8;

    doc.setFont(undefined, 'normal');
    const errorCounts = errors.reduce((acc, error) => {
      acc[error.category] = (acc[error.category] || 0) + 1;
      return acc;
    }, {});

    Object.entries(errorCounts).forEach(([category, count]) => {
      doc.text(
        `${category.charAt(0).toUpperCase() + category.slice(1)}: ${count} error(s)`,
        margin + 10,
        yPosition,
      );
      yPosition += 7;
    });
    yPosition += 10;

    // Error details
    doc.setFont(undefined, 'bold');
    doc.text(
      intl.formatMessage({
        id: 'ui-plugin-inventory-templates.errorReport.errorDetails',
      }),
      margin,
      yPosition,
    );
    yPosition += 10;

    // Process each error
    errors.forEach((error, index) => {
      // Check if we need a new page
      if (yPosition > pageHeight - 40) {
        doc.addPage();
        yPosition = margin;
      }

      // Error header
      doc.setFont(undefined, 'bold');
      doc.setFillColor(240, 240, 240);
      doc.rect(
        margin - 5,
        yPosition - 5,
        pageWidth - 2 * margin + 10,
        8,
        'F',
      );
      doc.text(
        intl.formatMessage(
          { id: 'ui-plugin-inventory-templates.errorReport.errorNumber' },
          { number: index + 1 },
        ),
        margin,
        yPosition,
      );
      yPosition += 10;

      // Error details
      doc.setFont(undefined, 'normal');
      doc.setFontSize(10);

      const details = [
        intl.formatMessage(
          { id: 'ui-plugin-inventory-templates.errorReport.category' },
          { category: error.category },
        ),
        intl.formatMessage(
          { id: 'ui-plugin-inventory-templates.errorReport.operation' },
          {
            operation:
              error.operation ||
              intl.formatMessage({
                id: 'ui-plugin-inventory-templates.errorReport.unknownOperation',
              }),
          },
        ),
        intl.formatMessage(
          { id: 'ui-plugin-inventory-templates.errorReport.time' },
          { time: new Date(error.timestamp).toLocaleTimeString() },
        ),
        error.code !== 'UNKNOWN'
          ? intl.formatMessage(
              { id: 'ui-plugin-inventory-templates.errorReport.errorCode' },
              { code: error.code },
            )
          : '',
      ].filter((line) => line);

      details.forEach((detail) => {
        doc.text(detail, margin + 5, yPosition);
        yPosition += 6;
      });

      // Error message (wrap long text)
      doc.text(
        intl.formatMessage({
          id: 'ui-plugin-inventory-templates.errorReport.messageLabel',
        }),
        margin + 5,
        yPosition,
      );
      yPosition += 6;

      const messageLines = doc.splitTextToSize(
        getTranslatedErrorMessage(error, intl) ||
          intl.formatMessage({
            id: 'ui-plugin-inventory-templates.errorReport.noMessage',
          }),
        pageWidth - 2 * margin - 10,
      );
      messageLines.forEach((line) => {
        if (yPosition > pageHeight - 20) {
          doc.addPage();
          yPosition = margin;
        }
        doc.text(line, margin + 10, yPosition);
        yPosition += 5;
      });

      yPosition += 10;
    });

    // Save the PDF
    doc.save(filename);
    console.log('PDF exported successfully:', filename);
  } catch (error) {
    console.error('Failed to generate PDF:', error);
    // Fallback to text export
    exportErrorsToText(errors, contextData, mode, intl);
  }
};

/**
 * Copy errors to clipboard
 * @param {Array} errors - Array of error objects
 * @returns {Promise<boolean>} - Success status
 */
export const copyErrorsToClipboard = async (errors, intl) => {
  if (errors.length === 0) return false;

  const errorText = errors
    .map((error) => {
      const timestamp = error.timestamp
        ? new Date(error.timestamp).toLocaleString()
        : '';
      const timestampStr = timestamp ? `[${timestamp}] ` : '';
      const categoryStr = error.category
        ? `${error.category.toUpperCase()} - `
        : '';
      const operationStr = error.operation ? `${error.operation}: ` : '';
      const codeStr =
        error.code && error.code !== 'UNKNOWN'
          ? ` (Code: ${error.code})`
          : '';

      return `${timestampStr}${categoryStr}${operationStr}${getTranslatedErrorMessage(error, intl)}${codeStr}`;
    })
    .join('\n');

  try {
    await navigator.clipboard.writeText(errorText);
    return true;
  } catch (err) {
    // Fallback for older browsers
    const textArea = document.createElement('textarea');
    textArea.value = errorText;
    document.body.appendChild(textArea);
    textArea.select();
    const success = document.execCommand('copy');
    document.body.removeChild(textArea);
    return success;
  }
};